import React, { useState, useEffect } from 'react';
import { complianceService } from '../../../../services/api';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    certificate: any;
    propertyId: string;
    onSuccess: () => void;
}

const EditCertificateModal: React.FC<Props> = ({
    isOpen,
    onClose,
    certificate,
    propertyId,
    onSuccess
}) => {
    const [expiryDate, setExpiryDate] = useState('');
    const [issuedDate, setIssuedDate] = useState('');
    const [certType, setCertType] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Unified certificate types for consistency
    const certificateTypes = [
        'Gas Safety',
        'Electrical (EICR)',
        'Energy (EPC)',
        'Landlord Insurance',
        'Tenancy Agreement',
        'Council Tax',
        'Water Bill',
        'Right to Rent Check',
        'HMO License',
        'Smoke Alarms',
        'Carbon Monoxide',
        'PAT Testing',
        'Legionella Risk',
        'Other'
    ];

    const isCoreCertificate = certificate ? ['Gas Safety', 'Electrical (EICR)', 'Energy (EPC)', 'Landlord Insurance', 'Tenancy Agreement'].includes(certificate.type) : false;

    useEffect(() => {
        if (certificate) {
            setExpiryDate(certificate.expires ? new Date(certificate.expires).toISOString().split('T')[0] : '');
            setIssuedDate(certificate.issued ? new Date(certificate.issued).toISOString().split('T')[0] : '');
            setCertType(certificate.type || '');
        }
    }, [certificate]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!expiryDate) {
            setError('Expiry date is required');
            return;
        }

        setLoading(true);
        try {
            await complianceService.updateCertificate(certificate.id, propertyId, {
                expiryDate,
                issuedDate,
                type: certType
            });
            onSuccess();
            onClose();
        } catch (err: any) {
            console.error('[EditCert] Error:', err);
            setError(err.response?.data?.message || 'Failed to update certificate');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen || !certificate) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>
                <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
                <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                    <div>
                        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-indigo-100">
                            <i className="fas fa-edit text-indigo-600"></i>
                        </div>
                        <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                            <h3 className="text-lg leading-6 font-medium text-gray-900">
                                Edit Certificate Details
                            </h3>
                            <form onSubmit={handleSubmit} className="mt-4 space-y-4">
                                {error && (
                                    <div className="bg-red-50 p-2 text-red-700 text-sm rounded border border-red-200">
                                        {error}
                                    </div>
                                )}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Certificate Type (Label)</label>
                                    <select
                                        value={certType}
                                        onChange={(e) => setCertType(e.target.value)}
                                        disabled={isCoreCertificate}
                                        className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm font-semibold ${isCoreCertificate
                                            ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                                            : 'text-purple-700'
                                            }`}
                                    >
                                        {certificateTypes.map(t => (
                                            <option key={t} value={t}>{t}</option>
                                        ))}
                                    </select>
                                    {isCoreCertificate ? (
                                        <p className="mt-1 text-[10px] text-orange-600 font-medium italic animate-pulse">
                                            <i className="fas fa-lock mr-1"></i> This is a Core Compliance Category and cannot be renamed.
                                        </p>
                                    ) : (
                                        <p className="mt-1 text-[10px] text-gray-400">You can change the label for supplemental documents.</p>
                                    )}
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Expiry Date</label>
                                        <input
                                            type="date"
                                            value={expiryDate}
                                            onChange={(e) => setExpiryDate(e.target.value)}
                                            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Issued Date</label>
                                        <input
                                            type="date"
                                            value={issuedDate}
                                            onChange={(e) => setIssuedDate(e.target.value)}
                                            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                        />
                                    </div>
                                </div>
                                <div className="mt-5 sm:mt-6 flex space-x-3">
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        className="flex-1 inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="flex-1 inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm disabled:opacity-50"
                                    >
                                        {loading ? 'Saving...' : 'Save Changes'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditCertificateModal;
